"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Heart, MapPin, X } from "lucide-react";
import { StrapiImage } from "@/components/StrapiImage";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useFavoriteServices } from "@/hooks/useFavoriteServices";
import {
  fetchMarketplaceServices,
  serviceCarouselImageUrls,
  serviceDetailPath,
  type MarketplaceService,
} from "@/lib/marketplace-services";

export type MarketplaceFavoritesSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When the catalog is already in memory, filter locally instead of fetching. */
  catalogServices?: MarketplaceService[];
};

export function MarketplaceFavoritesSheet({
  open,
  onOpenChange,
  catalogServices = [],
}: MarketplaceFavoritesSheetProps) {
  const { favoriteIds, toggleFavorite } = useFavoriteServices();
  const [favoriteServices, setFavoriteServices] = useState<MarketplaceService[]>(
    [],
  );
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesUiReady, setFavoritesUiReady] = useState(false);

  useEffect(() => {
    setFavoritesUiReady(true);
  }, []);

  const favCount = favoritesUiReady ? favoriteIds.size : 0;

  useEffect(() => {
    if (!open) return;
    if (catalogServices.length > 0) {
      setFavoriteServices(
        catalogServices.filter((service) => favoriteIds.has(service.id)),
      );
      setFavoritesLoading(false);
      return;
    }

    let cancelled = false;
    setFavoritesLoading(true);
    fetchMarketplaceServices()
      .then((all) => {
        if (cancelled) return;
        setFavoriteServices(
          all.filter((service) => favoriteIds.has(service.id)),
        );
      })
      .catch(() => {
        if (!cancelled) setFavoriteServices([]);
      })
      .finally(() => {
        if (!cancelled) setFavoritesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, favoriteIds, catalogServices]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader className="border-b border-border/70 pb-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Heart className="h-5 w-5" />
            Sačuvane usluge
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 pb-7 md:!px-7 py-4 pt-0">
          {favoritesLoading ? (
            <p className="text-sm text-muted-foreground">Učitavanje...</p>
          ) : favCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              Još nema sačuvanih usluga. Dodirnite srce na kartici usluge da je
              sačuvate.
            </p>
          ) : favoriteServices.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nije moguće učitati usluge. Proverite konekciju ili pokušajte
              ponovo.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {favoriteServices.map((service) => (
                <li key={service.documentId ?? service.id} className="py-5">
                  <Link
                    href={serviceDetailPath(service)}
                    className="group flex items-center gap-3 rounded-xl px-1 py-1 transition-colors hover:bg-muted/40"
                    onClick={() => onOpenChange(false)}
                  >
                    <div className="relative h-[80px] w-[80px] shrink-0 overflow-hidden rounded-md bg-muted">
                      <StrapiImage
                        src={
                          serviceCarouselImageUrls(service)[0] ??
                          "/placeholder.svg"
                        }
                        alt={service.title}
                        fill
                        sizes="80px"
                        className="object-cover"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-base font-semibold leading-[1.15] tracking-tight text-black hover:text-primary dark:text-white dark:hover:text-white/90">
                        {service.title}
                      </p>
                      <div className="mt-2 inline-flex h-10 items-center gap-1.5 rounded-[10px] bg-[#F7F8FF] px-3 text-base font-semibold leading-none text-[#111111] dark:bg-white/10 dark:text-white">
                        <MapPin className="h-4 w-4 shrink-0 text-[#000000] dark:text-white" />
                        <span className="text-xs font-medium leading-[1.5] text-[#111111] dark:text-white">
                          {(
                            service.locations?.[0]?.locationName ?? "Lokacija"
                          ).trim() || "Lokacija"}
                        </span>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      noScale
                      className="h-8 w-8 shrink-0 rounded-md bg-[#F7F8FF] text-black/90 transition-colors hover:bg-[#ECEEFA] dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
                      aria-label="Ukloni iz omiljenih"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        await toggleFavorite(service);
                      }}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
